/**
 * Analysis Report Component
 * Renders Gemini MoE analysis results with styled markdown
 * Includes drill-down buttons for expert sections
 *
 * @module AnalysisReport
 * Ported from V2: theWIRE/SLAMPitchDashboard/components/analyzer/AnalysisReport.tsx
 */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { cn } from '../../../lib/utils';
import {
  exportToJSON,
  exportToCSV,
  exportToPDF,
  exportToMarkdown,
  exportToTXT,
  exportToDOCX,
  exportViaEmail
} from '../../../lib/exportService';

// Expert names that can be drilled down
const DRILLDOWN_EXPERTS = [
  'BIOMECHANICS_EXPERT',
  'VISUAL_ANALYSIS_EXPERT',
  'EXPLOITATION_STRATEGY_EXPERT',
  'GAME_DYNAMICS_EXPERT',
  'BROADCAST_EXPERT',
  'STATISTICAL_EXPERT',
];

// Map section headings to expert names
const detectExpertFromHeading = (heading) => {
  if (!heading) return null;
  const upper = heading.toUpperCase();
  for (const expert of DRILLDOWN_EXPERTS) {
    if (upper.includes(expert.replace(/_/g, ' ')) || upper.includes(expert)) {
      return expert;
    }
  }
  return null;
};

// Risk level colors - using SLAM design tokens (black + neon gold)
const RISK_COLORS = {
  CRITICAL: 'bg-red-500 text-white',
  HIGH: 'bg-yellow-600 text-black',
  MEDIUM: 'bg-yellow-500 text-black',
  LOW: 'bg-green-500 text-white',
  NONE: 'bg-yellow-400 text-black',
};

// Section icons based on content
const SECTION_ICONS = {
  summary: '\uD83D\uDCCB',
  executive: '\uD83D\uDCCB',
  risk: '\u26A0\uFE0F',
  tipping: '\u26A0\uFE0F',
  tell: '\uD83D\uDD0D',
  detection: '\uD83D\uDD0D',
  pitch: '\u26BE',
  exploitation: '\uD83C\uDFAF',
  biomech: '\uD83E\uDDAD',
  motion: '\uD83E\uDDAD',
  visual: '\uD83D\uDC41\uFE0F',
  strategy: '\uD83C\uDFAF',
  game: '\uD83C\uDFC8',
  recommend: '\uD83D\uDCA1',
  confidence: '\uD83D\uDCCA',
  statistic: '\uD83D\uDCCA',
  expert: '\uD83E\uDDD1\u200D\uD83D\uDD2C',
};

/**
 * Get icon for section based on heading text
 * @param {string} heading - Section heading
 * @returns {string} Icon emoji
 */
function getSectionIcon(heading) {
  const lower = heading.toLowerCase();
  for (const [keyword, icon] of Object.entries(SECTION_ICONS)) {
    if (lower.includes(keyword)) return icon;
  }
  return '\uD83D\uDCC4';
}

/**
 * Parse risk level from text
 * @param {string} text - Text to search
 * @returns {string|null} Risk level if found
 */
function parseRiskLevel(text) {
  const match = text.match(/\b(CRITICAL|HIGH|MEDIUM|LOW|NONE)\b/i);
  return match ? match[1].toUpperCase() : null;
}

/**
 * Analysis Report Component
 * @param {Object} props
 * @param {string} props.markdown - Raw analysis markdown
 * @param {boolean} [props.isLoading] - Whether analysis is in progress
 * @param {string} [props.className] - Additional CSS classes
 * @param {Array} [props.mediaItems] - Media items for export
 * @param {Function} [props.onDrillDown] - Callback when drill-down is requested (expertName) => void
 * @param {boolean} [props.isDrillDownLoading] - Whether a drill-down is in progress
 */
export function AnalysisReport({ markdown, isLoading = false, className, mediaItems = [], onDrillDown, isDrillDownLoading = false, title = 'Analysis Report' }) {
  const [copySuccess, setCopySuccess] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    if (!showExportMenu) return;
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const handleExportPDF = () => {
    exportToPDF(markdown);
    setShowExportMenu(false);
  };

  const handleExportJSON = () => {
    exportToJSON(markdown, mediaItems);
    setShowExportMenu(false);
  };

  const handleExportCSV = () => {
    exportToCSV(markdown, mediaItems);
    setShowExportMenu(false);
  };

  const handleExportMarkdown = () => {
    exportToMarkdown(markdown);
    setShowExportMenu(false);
  };

  const handleExportTXT = () => {
    exportToTXT(markdown);
    setShowExportMenu(false);
  };

  const handleExportDOCX = async () => {
    await exportToDOCX(markdown, mediaItems);
    setShowExportMenu(false);
  };

  const handleExportEmail = () => {
    exportViaEmail(markdown, mediaItems);
    setShowExportMenu(false);
  };

  const handleCopyToClipboard = async () => {
    try {
      // Copy the markdown directly (preserves headers and formatting)
      await navigator.clipboard.writeText(markdown);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
    setShowExportMenu(false);
  };
  // Parse markdown into sections
  const sections = useMemo(() => {
    if (!markdown) return [];

    const lines = markdown.split('\n');
    const result = [];
    let currentSection = { heading: '', content: [], level: 0 };

    for (const line of lines) {
      const h1Match = line.match(/^# (.+)$/);
      const h2Match = line.match(/^## (.+)$/);
      const h3Match = line.match(/^### (.+)$/);
      const h4Match = line.match(/^#### (.+)$/);
      const h5Match = line.match(/^##### (.+)$/);
      const h6Match = line.match(/^###### (.+)$/);

      if (h1Match || h2Match || h3Match || h4Match || h5Match || h6Match) {
        // Save previous section
        if (currentSection.heading || currentSection.content.length > 0) {
          result.push(currentSection);
        }

        // Start new section - detect heading level
        const match = h1Match || h2Match || h3Match || h4Match || h5Match || h6Match;
        let level = 1;
        if (h2Match) level = 2;
        else if (h3Match) level = 3;
        else if (h4Match) level = 4;
        else if (h5Match) level = 5;
        else if (h6Match) level = 6;

        currentSection = {
          heading: match[1],
          content: [],
          level,
          icon: getSectionIcon(match[1]),
          risk: parseRiskLevel(match[1]),
        };
      } else {
        currentSection.content.push(line);
      }
    }

    // Push final section
    if (currentSection.heading || currentSection.content.length > 0) {
      result.push(currentSection);
    }

    return result;
  }, [markdown]);

  // Render inline markdown formatting
  const renderInlineMarkdown = (text) => {
    if (!text) return null;

    // Process bold, italic, code, links
    const parts = [];
    let remaining = text;
    let key = 0;

    // Simple regex-based parsing
    while (remaining.length > 0) {
      // Bold
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      if (boldMatch && boldMatch.index === 0) {
        parts.push(<strong key={key++} className="font-bold text-white">{boldMatch[1]}</strong>);
        remaining = remaining.slice(boldMatch[0].length);
        continue;
      }

      // Code
      const codeMatch = remaining.match(/`(.+?)`/);
      if (codeMatch && codeMatch.index === 0) {
        parts.push(
          <code key={key++} className="px-1 py-0.5 bg-gray-700 rounded text-yellow-300 font-mono text-sm">
            {codeMatch[1]}
          </code>
        );
        remaining = remaining.slice(codeMatch[0].length);
        continue;
      }

      // Risk badges inline
      const riskMatch = remaining.match(/\[(CRITICAL|HIGH|MEDIUM|LOW|NONE)\]/);
      if (riskMatch && riskMatch.index === 0) {
        const level = riskMatch[1];
        parts.push(
          <span key={key++} className={cn('px-2 py-0.5 rounded-full text-xs font-bold', RISK_COLORS[level])}>
            {level}
          </span>
        );
        remaining = remaining.slice(riskMatch[0].length);
        continue;
      }

      // Regular text until next special character
      const nextSpecial = remaining.search(/\*\*|`|\[(?:CRITICAL|HIGH|MEDIUM|LOW|NONE)\]/);
      if (nextSpecial === -1) {
        parts.push(remaining);
        break;
      } else if (nextSpecial > 0) {
        parts.push(remaining.slice(0, nextSpecial));
        remaining = remaining.slice(nextSpecial);
      } else {
        // No match at start, consume one character
        parts.push(remaining[0]);
        remaining = remaining.slice(1);
      }
    }

    return parts;
  };

  // Render table from markdown
  const renderTable = (lines) => {
    if (lines.length < 2) return null;

    const parseRow = (line) =>
      line.split('|').filter(cell => cell.trim()).map(cell => cell.trim());

    const headers = parseRow(lines[0]);
    // Skip separator line (index 1)
    const rows = lines.slice(2).map(parseRow);

    return (
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-gray-600">
              {headers.map((header, i) => (
                <th key={i} className="px-3 py-2 text-left text-gray-300 font-medium">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                {row.map((cell, cellIdx) => (
                  <td key={cellIdx} className="px-3 py-2 text-gray-400">
                    {renderInlineMarkdown(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render section content
  const renderContent = (content) => {
    const elements = [];
    let tableLines = [];
    let inTable = false;
    let listItems = [];
    let inList = false;
    let key = 0;

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={key++} className="list-disc list-inside space-y-1 my-2 text-gray-300">
            {listItems.map((item, i) => (
              <li key={i}>{renderInlineMarkdown(item)}</li>
            ))}
          </ul>
        );
        listItems = [];
      }
    };

    const flushTable = () => {
      if (tableLines.length > 0) {
        elements.push(<React.Fragment key={key++}>{renderTable(tableLines)}</React.Fragment>);
        tableLines = [];
      }
    };

    for (const line of content) {
      const trimmedLine = line.trim();

      // Detect table
      if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
        flushList();
        inTable = true;
        tableLines.push(trimmedLine);
        continue;
      } else if (inTable) {
        flushTable();
        inTable = false;
      }

      // Detect list
      if (trimmedLine.match(/^[-*]\s/)) {
        flushTable();
        inList = true;
        listItems.push(trimmedLine.replace(/^[-*]\s/, ''));
        continue;
      } else if (inList && trimmedLine.match(/^\d+\.\s/)) {
        // Numbered list item
        listItems.push(trimmedLine.replace(/^\d+\.\s/, ''));
        continue;
      } else if (inList && trimmedLine === '') {
        flushList();
        inList = false;
        continue;
      } else if (inList) {
        flushList();
        inList = false;
      }

      // Empty line
      if (trimmedLine === '') {
        elements.push(<div key={key++} className="h-2" />);
        continue;
      }

      // Numbered list start
      if (trimmedLine.match(/^\d+\.\s/)) {
        inList = true;
        listItems.push(trimmedLine.replace(/^\d+\.\s/, ''));
        continue;
      }

      // Horizontal rule
      if (trimmedLine === '---' || trimmedLine === '***') {
        elements.push(<hr key={key++} className="border-gray-700 my-4" />);
        continue;
      }

      // Regular paragraph
      elements.push(
        <p key={key++} className="text-gray-300 leading-relaxed my-1">
          {renderInlineMarkdown(trimmedLine)}
        </p>
      );
    }

    flushList();
    flushTable();

    return elements;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('p-6 bg-gray-800/50 rounded-lg border border-gray-700', className)}>
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-gray-300">Analyzing media with Gemini MoE...</span>
        </div>
        <div className="mt-4 space-y-2">
          <div className="h-4 bg-gray-700 rounded animate-pulse w-3/4" />
          <div className="h-4 bg-gray-700 rounded animate-pulse w-1/2" />
          <div className="h-4 bg-gray-700 rounded animate-pulse w-5/6" />
        </div>
      </div>
    );
  }

  // Empty state
  if (!markdown) {
    return (
      <div className={cn('p-6 bg-gray-800/50 rounded-lg border border-gray-700 text-center', className)}>
        <div className="text-4xl mb-3">\uD83D\uDCCA</div>
        <div className="text-gray-400">
          Analysis results will appear here after processing
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Export Actions Bar */}
      {markdown && (
        <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">{title}</span>
            {copySuccess && (
              <span className="text-xs text-green-400 flex items-center gap-1">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 13l4 4L19 7" />
                </svg>
                Copied!
              </span>
            )}
          </div>
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/40 text-yellow-400 rounded-lg text-sm font-medium transition-all"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
              </svg>
              Export
              <svg className={`w-3 h-3 transition-transform ${showExportMenu ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {showExportMenu && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
                {/* Documents Group */}
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-medium">Documents</div>
                <button
                  onClick={handleExportPDF}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-left"
                >
                  <svg className="w-4 h-4 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  PDF
                </button>
                <button
                  onClick={handleExportDOCX}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-left"
                >
                  <svg className="w-4 h-4 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <path d="M9 13h6M9 17h4" />
                  </svg>
                  DOCX
                </button>
                <button
                  onClick={handleExportMarkdown}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-left"
                >
                  <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <path d="M7 13l3-3 3 3M13 11l2-2 2 2" />
                  </svg>
                  Markdown
                </button>
                <button
                  onClick={handleExportTXT}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-left"
                >
                  <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="8" y1="13" x2="16" y2="13" />
                    <line x1="8" y1="17" x2="12" y2="17" />
                  </svg>
                  Plain Text
                </button>

                {/* Data Group */}
                <div className="border-t border-gray-700" />
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-medium">Data</div>
                <button
                  onClick={handleExportJSON}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-left"
                >
                  <svg className="w-4 h-4 text-yellow-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <path d="M8 16s1-2 4-2 4 2 4 2" />
                    <path d="M8 12s1-2 4-2 4 2 4 2" />
                  </svg>
                  JSON
                </button>
                <button
                  onClick={handleExportCSV}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-left"
                >
                  <svg className="w-4 h-4 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="8" y1="13" x2="16" y2="13" />
                    <line x1="8" y1="17" x2="16" y2="17" />
                  </svg>
                  CSV
                </button>

                {/* Share Group */}
                <div className="border-t border-gray-700" />
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-medium">Share</div>
                <button
                  onClick={handleExportEmail}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-left"
                >
                  <svg className="w-4 h-4 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M22 6l-10 7L2 6" />
                  </svg>
                  Email
                </button>
                <button
                  onClick={handleCopyToClipboard}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-left"
                >
                  <svg className="w-4 h-4 text-purple-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                  </svg>
                  Copy Text
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {sections.map((section, idx) => {
        // Check if this section is from an expert (for drill-down)
        const expertName = detectExpertFromHeading(section.heading);

        return (
          <div
            key={idx}
            className={cn(
              'p-4 bg-gray-800/50 rounded-lg border border-gray-700',
              section.level === 1 && 'border-yellow-500/30',
              section.risk === 'CRITICAL' && 'border-red-500/50 bg-red-900/10',
              section.risk === 'HIGH' && 'border-yellow-500/50 bg-yellow-900/10'
            )}
          >
            {section.heading && (
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">{section.icon}</span>
                <h3
                  className={cn(
                    'font-bold flex-1',
                    section.level === 1 && 'text-xl text-white',
                    section.level === 2 && 'text-lg text-gray-100',
                    section.level === 3 && 'text-base text-gray-200',
                    section.level === 4 && 'text-sm text-gray-300',
                    section.level === 5 && 'text-sm text-gray-400',
                    section.level === 6 && 'text-xs text-gray-400 uppercase tracking-wide'
                  )}
                >
                  {section.heading}
                </h3>
                {section.risk && (
                  <span className={cn('px-2 py-0.5 rounded-full text-xs font-bold', RISK_COLORS[section.risk])}>
                    {section.risk}
                  </span>
                )}
                {/* Drill Down button for expert sections */}
                {expertName && onDrillDown && (
                  <button
                    onClick={() => onDrillDown(expertName)}
                    disabled={isDrillDownLoading}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-lg transition-all',
                      isDrillDownLoading
                        ? 'bg-gray-600 text-gray-400 cursor-wait'
                        : 'bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 text-cyan-400 hover:text-cyan-300'
                    )}
                  >
                    {isDrillDownLoading ? (
                      <>
                        <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          <path d="M10 10l4 4" />
                        </svg>
                        Drill Down
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
            <div className="text-sm">{renderContent(section.content)}</div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Confidence Meter Component
 * @param {Object} props
 * @param {string} props.label - Meter label
 * @param {number} props.value - Value (0-100)
 * @param {string} [props.className] - Additional CSS classes
 */
export function ConfidenceMeter({ label, value, className }) {
  const getColor = (v) => {
    if (v >= 80) return 'bg-green-500';
    if (v >= 60) return 'bg-yellow-500';
    if (v >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">{label}</span>
        <span className="text-white font-medium">{value}%</span>
      </div>
      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500', getColor(value))}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

export default AnalysisReport;
